import React from 'react';
import { PLAN_PRICES_USD, entryPlans, formatUsd, subscriptionPlans } from '../../data/pricingPlans';
import type { CheckoutPlanId } from '../../data/pricingPlans';
import { PricingCard } from './PricingCard';
interface PricingPlansProps {
  onCheckout: (plan: CheckoutPlanId) => void;
  pendingPlan?: CheckoutPlanId | null;
  /** Where the free plan CTA sends the visitor. */
  freeHref?: string;
  /**
   * row    — entry tiers as wide horizontal strips
   * column — entry tiers as vertical cards in a grid, matching the
   *          unlimited plans below
   */
  entryLayout?: 'row' | 'column';
}
/**
 * The complete five-tier pricing system, grouped by what the buyer is
 * actually deciding: try it once vs. unlimited access. Monthly carries the
 * dominant treatment inside the unlimited row.
 */
export function PricingPlans({
  onCheckout,
  pendingPlan = null,
  freeHref = '/signup',
  entryLayout = 'row'
}: PricingPlansProps) {
  return <div className="space-y-12" data-id="element-1702" data-mp="1v5lbhva3wdt4">
      <section aria-labelledby="pricing-entry-heading" data-id="element-1703" data-mp="ivrksplvfc5j">
        <div className="mb-5 flex items-baseline justify-between gap-4" data-id="element-1704" data-mp="iugeai11jqaq">
          <h3 id="pricing-entry-heading" className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-gray-500" data-id="element-1705" data-mp="dxgbcfzhn3nu">
            First time? Start here
          </h3>
          <p className="hidden font-mono text-[11px] uppercase tracking-[0.12em] text-gray-600 sm:block" data-id="element-1706" data-mp="4uyl7qgppqvk">
            No subscription
          </p>
        </div>
        <p className="mb-5 max-w-2xl text-base text-gray-300" data-id="element-1707" data-mp="3rajgi89dwmro">
          Need the complete analysis for this conversation?{' '}
          <span className="font-bold text-white" data-id="element-1708" data-mp="3mcrjiztvuagm">
            {formatUsd(PLAN_PRICES_USD.single_scan)} one-time.
          </span>{' '}
          No subscription required.
        </p>
        <div className={`grid items-stretch gap-5 ${entryLayout === 'column' ? 'md:grid-cols-2' : 'lg:grid-cols-2'}`} data-id="element-1709" data-mp="3da8kyrx4qk06">
          {entryPlans.map(plan => <PricingCard key={plan.id} plan={plan} layout={entryLayout} href={plan.id === 'free' ? freeHref : undefined} onCheckout={onCheckout} isLoading={pendingPlan === plan.id} disabled={pendingPlan !== null} data-id="element-1710" data-mp="2ynjmmtem6cvd" />)}
        </div>
      </section>

      <section aria-labelledby="pricing-unlimited-heading" data-id="element-1711" data-mp="3hfchyuh5oa69">
        <div className="mb-5 flex items-baseline justify-between gap-4" data-id="element-1712" data-mp="3hfuku9ybpmn5">
          <h3 id="pricing-unlimited-heading" className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] text-gray-500" data-id="element-1713" data-mp="3md4anqpmk0bi">
            Unlimited access — for repeat analysis
          </h3>
          <p className="hidden font-mono text-[11px] uppercase tracking-[0.12em] text-gray-600 sm:block" data-id="element-1714" data-mp="3cm9loekzummd">
            Cancel anytime
          </p>
        </div>
        <p className="mb-5 max-w-2xl text-sm text-gray-400" data-id="element-1715" data-mp="90avvn4oqqdw">
          Analyzing more than one conversation, or tracking how a relationship
          changes over time? Subscriptions include every full report.
        </p>
        <div className="grid items-stretch gap-5 md:grid-cols-2 lg:grid-cols-3 lg:pt-3" data-id="element-1716" data-mp="1oq1zwebuhd8o">
          {subscriptionPlans.map(plan => <PricingCard key={plan.id} plan={plan} onCheckout={onCheckout} isLoading={pendingPlan === plan.id} disabled={pendingPlan !== null} data-id="element-1717" data-mp="1ygeii103mwc6" />)}
        </div>
      </section>
    </div>;
}